import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Context } from '../context';
import { SettingsService } from './settings.service';
import { partialInit } from '../utils/partial-init';
import { UserModel } from '../models/user.model';
import { PlanModel, SubscriptionStatus } from '../models/plan.model';
import { EMPTY, defer, from, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class PlanService {

  constructor(private api: ApiService,
    private settings: SettingsService
  ) {}

  async init() {
    if (!this.hasPlan()) {
      await this.clearPlan();
      return;
    }

    const plan = await this.settings.plan() ?? 
    await this.api.getCurrentPlan();
    await this.applyPlan(plan);
  }

  canPlaySong() {
    if (!this.hasPlan()) {
      return Promise.resolve(true);
    }

    return this.api.canPlaySong();
  }

  getCurrentPlan() {
    if (!this.hasPlan()) {
      return Promise.resolve(null);
    }

    return this.refreshPlan();
  }

  async syncPlan() {
    if (!this.hasPlan()) {
      await this.clearPlan();
      return Context.user;
    }

    const user = await this.applyUserResult(this.api.syncUserPlan());

    if (!this.hasPlan()) {
      await this.clearPlan();
      return user;
    }

    await this.refreshPlan();
    return user;
  }

  getUpgradePlans() {
    if (!this.hasPlan()) {
      return Promise.resolve([]);
    }

    return this.api.getUpgradePlans();
  }

  upgradePlan(planId: number) {
    return defer(() => {
      this.assertHasPlan();
      return from(this.api.upgradePlan(planId));
    }).pipe(
      switchMap(response => {
        if (response.approveUrl) {
          window.location.replace(response.approveUrl);
          return EMPTY;
        }

        return of({ key: response.key });
      })
    );
  }

  public updateStatus(status: SubscriptionStatus, reason: string) {
    return defer(() => {
      this.assertHasPlan();
      return from(this.api.updateStatus(status, reason));
    });
  }

  public processUpgrade(key: string) {
    return this.api.processUpgrade(key);
  }

  public syncSubscriptionStatus() {
    return defer(() => from(this.applyUserResult(this.api.syncUserPlan()))).pipe(
      map(user => user.subscriptionStatus)
    );
  }

  hasPlan() {
    return !!Context.user?.planId;
  }

  private async refreshPlan() {
    const plan = await this.api.getCurrentPlan();
    return this.applyPlan(plan);
  }

  private async applyPlan(plan: PlanModel) {
    if (Context.plan) {
      partialInit(Context.plan, plan);
    } else {
      Context.plan = plan;
    }

    await this.settings.plan(Context.plan);
    return Context.plan;
  }

  private async clearPlan() {
    Context.plan = null;
    await this.settings.removeByKey('plan');
  }

  private assertHasPlan() {
    if (!this.hasPlan()) {
      throw new Error('This user does not have a plan.');
    }
  }

  private async applyUserResult(request: Promise<UserModel>) {
    const user = await request;

    if (Context.user) {
      partialInit(Context.user, user);
    } else {
      Context.user = user;
    }

    await this.settings.user(Context.user);
    return user;
  }
}
